import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./leaderboard.css";

const formatCategory = (key) =>
  (key || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

function Leaderboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/getLeaderboard`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setList(data.data);
        } else {
          setError(data.error || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken]);

  const buttonColor = themeData?.button_color ?? "#6b91e8";
  const buttonTextColor = themeData?.button_text_color ?? "#ffffff";
  const textColor = themeData?.text_color ?? "#333";
  const bgImage =
    typeof window !== "undefined" && window.innerWidth <= 768 && themeData?.background_mob
      ? process.env.REACT_APP_S3_PATH + themeData.background_mob
      : process.env.REACT_APP_S3_PATH + (themeData?.background_desk || "");

  return (
    <div
      className="leaderboard-page"
      style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}
    >
      <div className="leaderboard-top-bar">
        <button
          type="button"
          className="leaderboard-back-btn"
          style={{ backgroundColor: buttonColor, color: buttonTextColor }}
          onClick={() => navigate("/health-sprint")}
        >
          Back
        </button>
      </div>

      <div className="leaderboard-container">
        <h2 className="leaderboard-title" style={{ color: textColor }}>
          Leaderboard
        </h2>

        {loading ? (
          <div className="leaderboard-loading">Loading...</div>
        ) : error ? (
          <div className="leaderboard-error">{error}</div>
        ) : (
          <div className="leaderboard-table-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Category</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="leaderboard-empty">
                      No claims yet.
                    </td>
                  </tr>
                ) : (
                  list.map((row) => (
                    <tr key={`${row.rank}-${row.timestamp}-${row.category}`}>
                      <td>{row.rank}</td>
                      <td>{row.name}</td>
                      <td>{row.email}</td>
                      <td>{formatCategory(row.category)}</td>
                      <td>{row.timestamp || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
